import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import { clearCache } from './utils/apiCache';

clearCache();

let instanceCounter = 0;

const toBoolean = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
    }

    return Boolean(value);
};

const parseElementConfig = (el) => {
    if (!el?.dataset?.config) {
        return {};
    }

    try {
        return JSON.parse(el.dataset.config);
    } catch (error) {
        return {};
    }
};

const getElementFallbackConfig = (el) => ({
    loanAmountLabel: el.dataset.loanAmountLabel,
    monthlyLabel: el.dataset.monthlyLabel,
    durationLabel: el.dataset.durationLabel,
    infoLinkLabel: el.dataset.infoLinkLabel,
    submitLabel: el.dataset.submitLabel,
    footerText: el.dataset.footerText,
    apiUrl: el.dataset.apiUrl,
    applyUrl: el.dataset.applyUrl,
    offerId: el.dataset.offerId,
    loginLabel: el.dataset.loginLabel,
    apiErrorMessage: el.dataset.apiErrorMessage,
    warningTitle: el.dataset.warningTitle,
    warningContent: el.dataset.warningContent,
    monthsLabel: el.dataset.monthsLabel,
    numDecimalMonthly: el.dataset.numDecimalMonthly,
    currency: el.dataset.currency,
    onlyApplyButton: el.dataset.onlyApplyButton,
    hideDuration: el.dataset.hideDuration,
    applyButtonId: el.dataset.applyButtonId,
    creditAccountLabel: el.dataset.creditAccountLabel
});

const normalizeConfig = (config = {}) => ({
    ...config,
    numDecimalMonthly: config.numDecimalMonthly ?? 0,
    onlyApplyButton: toBoolean(config.onlyApplyButton),
    hideDuration: toBoolean(config.hideDuration),
    fullDisclosureMode: toBoolean(config.fullDisclosureMode)
});

const mergeDefinedConfig = (...configs) => {
    const merged = {};

    configs.forEach((config) => {
        if (!config || typeof config !== 'object') {
            return;
        }

        Object.entries(config).forEach(([key, value]) => {
            if (value === '' || value === null || typeof value === 'undefined') {
                return;
            }

            merged[key] = value;
        });
    });

    return merged;
};

const buildProps = (el, runtimeConfig = {}) => {
    const globalConfig = window.CS_CALCULATOR_GLOBAL_CONFIG || {};
    const elementConfig = el ? {
        ...getElementFallbackConfig(el),
        ...parseElementConfig(el)
    } : {};
    const merged = normalizeConfig(mergeDefinedConfig(globalConfig, elementConfig, runtimeConfig));

    return {
        loanAmountLabel: merged.loanAmountLabel,
        monthlyLabel: merged.monthlyLabel,
        durationLabel: merged.durationLabel,
        infoLinkLabel: merged.infoLinkLabel,
        submitLabel: merged.submitLabel,
        footerText: merged.footerText,
        apiUrl: merged.apiUrl,
        applyUrl: merged.applyUrl,
        offerId: merged.offerId,
        loginLabel: merged.loginLabel,
        apiErrorMessage: merged.apiErrorMessage,
        warningTitle: merged.warningTitle,
        warningContent: merged.warningContent,
        monthsLabel: merged.monthsLabel,
        numDecimalMonthly: merged.numDecimalMonthly,
        siteCurrency: merged.siteCurrency || merged.currency,
        instanceId: instanceCounter++,
        onlyApplyButton: merged.onlyApplyButton,
        hideDuration: merged.hideDuration,
        applyButtonId: merged.applyButtonId,
        creditAccountLabel: merged.creditAccountLabel,
        fullDisclosureMode: merged.fullDisclosureMode,
        totalAmountLabel: merged.totalAmountLabel,
        representativeExampleTemplate: merged.representativeExampleTemplate
    };
};

const markFullDisclosureAncestors = (el) => {
    const parent = el?.parentElement;
    if (parent) {
        parent.classList.add('has-full-disclosure-calculator');
    }
};

const mountCalculator = (el, runtimeConfig = {}) => {
    if (!el) {
        return null;
    }

    const props = buildProps(el, runtimeConfig);

    if (props.fullDisclosureMode) {
        markFullDisclosureAncestors(el);
    }

    if (!el._reactRoot) {
        el._reactRoot = createRoot(el);
    }

    el._reactRoot.render(<App {...props} />);
    return el._reactRoot;
};

window.CSCalculator = {
    mount: mountCalculator
};

document.querySelectorAll('.calculator-root').forEach((el) => {
    mountCalculator(el);
});
